import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Rocket,
  Users,
  Tag,
  Package,
  Zap,
  Check,
  ArrowRight,
  PartyPopper,
  X,
  DollarSign
} from 'lucide-react'

interface CommercializationRoadmapModalProps {
  isOpen: boolean
  onClose: () => void
  onNavigate: (tab: string) => void
  hasPartners: boolean
  hasAttributionTags: boolean
  hasAssets: boolean
  hasActivations: boolean
}

interface StepConfig {
  id: string
  tab: string
  title: string
  subtitle: string
  description: string
  completedMessage: string
  icon: React.ElementType
  iconBg: string
  ctaText: string
}

const steps: StepConfig[] = [
  {
    id: 'partners',
    tab: 'partners',
    title: 'Add a Partner',
    subtitle: 'Who are you working with?',
    description: 'Partners are the brands, sponsors, and collaborators you work with. Add your first partner to start tracking sponsorship relationships.',
    completedMessage: 'You\'ve added your first partner! Now you can track all your sponsorship relationships.',
    icon: Users,
    iconBg: 'from-blue-500 to-indigo-600',
    ctaText: 'Add First Partner'
  },
  {
    id: 'tags',
    tab: 'attribution-tags',
    title: 'Create an Attribution Tag',
    subtitle: 'How will you track content?',
    description: 'Attribution tags (like #2026BaseballGameDay or #2026SoccerGameRecap) help you track which posts are part of your sponsorship campaigns and form the basis of Earned Media Value (EMV) tracking.',
    completedMessage: 'Attribution tag created! Your sponsored content will now be automatically tracked.',
    icon: Tag,
    iconBg: 'from-purple-500 to-violet-600',
    ctaText: 'Create Attribution Tag'
  },
  {
    id: 'assets',
    tab: 'assets',
    title: 'Add an Asset',
    subtitle: 'What inventory do you have?',
    description: 'Assets are your sponsorable inventory - jersey patches, stadium signage, social media posts, and more.',
    completedMessage: 'First asset added! Your sponsorship inventory is ready to sell.',
    icon: Package,
    iconBg: 'from-amber-500 to-orange-600',
    ctaText: 'Add First Asset'
  },
  {
    id: 'activations',
    tab: 'activations',
    title: 'Create an Activation',
    subtitle: 'Put it all together',
    description: 'Activations combine partners, assets, and tracking into complete sponsorship campaigns you can measure.',
    completedMessage: 'You\'re all set! Your first activation is live and ready to generate value.',
    icon: Zap,
    iconBg: 'from-green-500 to-emerald-600',
    ctaText: 'Create First Activation'
  }
]

const CommercializationRoadmapModal: React.FC<CommercializationRoadmapModalProps> = ({
  isOpen,
  onClose,
  onNavigate,
  hasPartners,
  hasAttributionTags,
  hasAssets,
  hasActivations
}) => {
  const stepStatus = {
    partners: hasPartners,
    tags: hasAttributionTags,
    assets: hasAssets,
    activations: hasActivations
  }

  const completedCount = Object.values(stepStatus).filter(Boolean).length
  const allComplete = completedCount === 4
  const progressPercentage = (completedCount / 4) * 100

  const handleStepClick = (tab: string) => {
    onNavigate(tab)
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleOverlayClick}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl bg-white"
          >
            <div className="p-8">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {allComplete ? (
                /* All Done Celebration */
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.6 }}
                    className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 mb-6"
                  >
                    <PartyPopper className="h-12 w-12 text-white" />
                  </motion.div>

                  <h2 className="text-3xl font-bold text-gray-900 mb-3">
                    You're All Set!
                  </h2>
                  <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
                    Congratulations! You've completed your commercialization setup and are ready to start tracking sponsorship value.
                  </p>

                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => onNavigate('dashboard')}
                      className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      View Dashboard
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onNavigate('activations')}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors shadow-sm"
                    >
                      <Zap className="mr-2 h-4 w-4" />
                      Create Activation
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Hero Section */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full text-blue-600 text-sm font-medium mb-4">
                      <Rocket className="h-4 w-4" />
                      Welcome to AVI
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Let's Get You Making Money
                    </h2>
                    <p className="text-gray-600 max-w-lg mx-auto text-sm">
                      Complete these 4 steps to set up your sponsorship management system and start tracking the value you generate.
                    </p>
                  </div>

                  {/* Money Motivation Banner */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <DollarSign className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-green-800 font-medium text-sm">Track Your Sponsorship Value</p>
                          <p className="text-green-600 text-xs">Complete setup to unlock full analytics</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{completedCount}/4</p>
                        <p className="text-gray-500 text-xs">steps complete</p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercentage}%` }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                      />
                    </div>
                  </div>

                  {/* Step Cards */}
                  <div className="space-y-3 mb-6">
                    {steps.map((step, index) => {
                      const isCompleted = stepStatus[step.id as keyof typeof stepStatus]
                      const Icon = step.icon

                      return (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`relative rounded-lg border p-4 transition-all ${
                            isCompleted
                              ? 'bg-green-50/50 border-green-200'
                              : 'bg-gray-50/50 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Step Number / Check */}
                            <div
                              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                                isCompleted
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-200 text-gray-500'
                              }`}
                            >
                              {isCompleted ? <Check className="h-4 w-4" /> : index + 1}
                            </div>

                            {/* Icon */}
                            <div
                              className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${step.iconBg} flex items-center justify-center`}
                            >
                              <Icon className="h-5 w-5 text-white" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <h3 className="text-gray-900 font-semibold">{step.title}</h3>
                                  <p className="text-gray-500 text-xs">{step.subtitle}</p>
                                </div>

                                {/* CTA Button */}
                                {!isCompleted && (
                                  <button
                                    onClick={() => handleStepClick(step.tab)}
                                    className="flex-shrink-0 inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors shadow-sm"
                                  >
                                    {step.ctaText}
                                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>

                              <p className="mt-1 text-gray-500 text-xs">
                                {isCompleted ? step.completedMessage : step.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={onClose}
                      className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
                    >
                      Skip for now
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default CommercializationRoadmapModal
